import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Divider,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { SaveOutlined, VerifiedUserOutlined } from "@mui/icons-material";
import useFormRol from "../../hooks/useFormRol";
import { getAvailableViews } from "shared/utils/getAvailableViews";
import PermissionNavigation from "../Permissions/PermissionNavigation";
import PermissionTree from "../Permissions/PermissionTree";
import RoleChangeSummary from "../Permissions/RoleChangeSummary";
import {
  branchNodes,
  buildPermissionTree,
  isEnabled,
  matchesPermission,
  permissionChanges,
  permissionCounts,
  togglePermission,
} from "../Permissions/permissionUtils";

const FormRol = ({ initialData, onSubmit, onCancel, isEditing }) => {
  const formik = useFormRol(initialData, onSubmit);
  const tree = useMemo(() => buildPermissionTree(getAvailableViews()), []);
  const [activeKey, setActiveKey] = useState(
    () =>
      tree.roots.find((node) =>
        branchNodes(node).some((item) => isEnabled(formik.values.more[item.key]))
      )?.key ||
      tree.roots[0]?.key ||
      ""
  );
  const [query, setQuery] = useState("");
  const [selectedOnly, setSelectedOnly] = useState(false);
  const modules = tree.roots.filter((node) =>
    matchesPermission(node, query, selectedOnly, formik.values.more)
  );
  const activeModule = modules.find((node) => node.key === activeKey) || modules[0];
  const changes = permissionChanges(formik.initialValues.more, formik.values.more, tree);

  const handleToggle = (key) => {
    formik.setValues((previous) => ({
      ...previous,
      more: togglePermission(previous.more, key, tree),
    }));
  };

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      noValidate
      sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}
    >
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, flexShrink: 0, bgcolor: "background.default" }}>
        <Typography
          variant="overline"
          sx={{ display: "block", mb: 1, fontWeight: 700, letterSpacing: "0.06em" }}
        >
          Datos del rol
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Nombre del rol"
          sx={{
            "& .MuiOutlinedInput-root": {
              minHeight: 44,
              borderRadius: 2,
              bgcolor: "background.paper",
            },
          }}
          name="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={formik.isSubmitting}
          error={Boolean(formik.touched.name && formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
        />
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mt: 2 }}>
          <TextField
            size="small"
            placeholder="Buscar permiso o módulo..."
            inputProps={{ "aria-label": "Buscar permiso o módulo" }}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{
              flex: "1 1 220px",
              "& .MuiOutlinedInput-root": {
                minHeight: 44,
                borderRadius: 2,
                bgcolor: "background.paper",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            sx={{
              m: 0,
              display: "inline-flex",
              alignItems: "center",
              whiteSpace: "nowrap",
              flexShrink: 0,
              px: 1.25,
              py: 0.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: selectedOnly ? "info.main" : "divider",
              bgcolor: selectedOnly ? "rgba(26,115,232,0.07)" : "background.paper",
            }}
            control={
              <Switch
                size="small"
                checked={selectedOnly}
                onChange={(event) => setSelectedOnly(event.target.checked)}
              />
            }
            label={<Typography variant="body2">Solo seleccionados</Typography>}
          />
        </Box>
        {!!formik.submitCount && typeof formik.errors.more === "string" && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {formik.errors.more}
          </Alert>
        )}
        {formik.status && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {formik.status}
          </Alert>
        )}
      </Box>
      <Divider sx={{ m: 0 }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          flex: 1,
          minHeight: 0,
          px: { xs: 2, md: 0 },
          pt: { xs: 2, md: 0 },
        }}
      >
        <PermissionNavigation
          modules={modules}
          activeKey={activeModule?.key || ""}
          onSelect={setActiveKey}
          permissions={formik.values.more}
        />
        <Box
          component="section"
          aria-label="Permisos del módulo"
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: "auto",
            p: { xs: 0, md: 2.5 },
            pb: 2,
            scrollbarWidth: "thin",
          }}
        >
          {activeModule ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                  mb: 2,
                  pb: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Permisos de {activeModule.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Seleccione los accesos que tendrá este rol.
                  </Typography>
                </Box>
                <Chip
                  icon={<VerifiedUserOutlined />}
                  label={`${permissionCounts(activeModule, formik.values.more).selected} / ${
                    permissionCounts(activeModule, formik.values.more).total
                  }`}
                  size="small"
                  sx={{
                    bgcolor: "rgba(26,115,232,0.08)",
                    color: "info.main",
                    fontWeight: 600,
                    "& .MuiChip-icon": { color: "info.main" },
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Marcar un grupo selecciona todos sus permisos.
              </Typography>
              <PermissionTree
                node={activeModule}
                permissions={formik.values.more}
                onToggle={handleToggle}
                query={query}
                selectedOnly={selectedOnly}
                disabled={formik.isSubmitting}
              />
            </>
          ) : (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Typography variant="subtitle1">No hay permisos que coincidan</Typography>
              <Typography variant="body2" color="text.secondary">
                Pruebe otra búsqueda o desactive «Solo seleccionados».
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          px: { xs: 2, sm: 3 },
          py: 2,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          bgcolor: "background.paper",
          boxShadow: "0 -4px 18px rgba(20,40,65,0.04)",
          "& .MuiButton-root": {
            minHeight: 42,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.8125rem",
            px: 2.5,
          },
        }}
      >
        <RoleChangeSummary
          changes={changes}
          nameChanged={formik.values.name !== formik.initialValues.name}
        />
        <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            sx={{
              color: "text.primary",
              borderColor: "divider",
              "&:hover": { bgcolor: "action.hover", borderColor: "text.secondary" },
            }}
            onClick={onCancel}
            disabled={formik.isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="info"
            sx={{
              bgcolor: "info.main",
              color: "#fff",
              "&:hover": { bgcolor: "info.dark" },
              "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "text.disabled" },
            }}
            disabled={formik.isSubmitting || (isEditing && !formik.dirty)}
            startIcon={
              formik.isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveOutlined />
              )
            }
          >
            {formik.isSubmitting ? "Guardando..." : isEditing ? "Guardar cambios" : "Crear rol"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
FormRol.propTypes = {
  initialData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
};
FormRol.defaultProps = { initialData: null, isEditing: false };
export default FormRol;
